import { DarvasBox as defaultOptions } from "./defaultOptionsForComputation";
import { slidingWindow } from "../utils";
import { sum } from "d3-array";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const {
      athLookbackPeriod,
      exitField,
      ghostBoxes,
      levelOffset,
      priceMinimum,
      volumeSpike,
      volumePercentOfAvg,
    } = options;

    // State variables
    let allTimeHighIndex = 0;
    let boxState = "none"; // "none", "high", "darvas"
    let boxData = {};
    let ghost = null;
    let buyLevel = null;
    let sellLevel = null;
    const spikePercentage = volumePercentOfAvg / 100;

    // Calculate volume average if needed
    let volumeAverage = null;
    if (volumeSpike) {
      const volumeMA = slidingWindow()
        .windowSize(athLookbackPeriod)
        .accumulator((values) => sum(values) / athLookbackPeriod);
      volumeAverage = volumeMA(data.map((d) => d.volume || 0));
    }

    const results = new Array(data.length);

    // Initialize all results
    for (let i = 0; i < data.length; i++) {
      results[i] = {
        darvasBox: null,
        ghostBox: null,
        lossLevel: null,
        profitLevel: null,
        athIndex: null,
        volumeSpikeMarker: null,
      };
    }

    // Process bars
    for (let i = 0; i < data.length; i++) {
      const quote = data[i];
      if (!quote) continue;

      // Check price minimum filter
      if (
        data[allTimeHighIndex] &&
        data[allTimeHighIndex].close >= priceMinimum
      ) {
        // Handle ghost box projection
        if (ghost && (!ghost.End || i === ghost.End + 1)) {
          if (i > 0 && data[i - 1].close > boxData.High) {
            // Project new ghost box
            // ChartIQ: End = 2 * boxData.End - boxData.Start + 1
            // But limit to available data length
            const projectedEnd = 2 * boxData.End - boxData.Start + 1;
            const limitedEnd = Math.min(projectedEnd, data.length - 1);

            boxData = {
              State: 1,
              High: 2 * boxData.High - boxData.Low,
              Low: boxData.High,
              Start: i,
              End: limitedEnd,
            };
          } else {
            ghost = null;
          }

          if (ghost) {
            results[i].ghostBox = { ...boxData };
            boxData.State = 0;
            if (boxData.End < data.length && data[boxData.End]) {
              results[boxData.End].ghostBox = { ...boxData };
            }
            ghost = { Start: boxData.Start, End: boxData.End };
            buyLevel = boxData.High + levelOffset;
            if (!sellLevel || sellLevel < boxData.Low - levelOffset) {
              sellLevel = boxData.Low - levelOffset;
            }
          }
        }

        // Store stop levels
        results[i].profitLevel = buyLevel;
        results[i].lossLevel = sellLevel;

        // Check buy level breakout
        if (buyLevel !== null) {
          if (quote.close >= buyLevel) {
            buyLevel = null;
          } else if (exitField === "high/low" && quote.high >= buyLevel) {
            buyLevel = null;
          }
        }

        // State: "none" -> waiting for high confirmation
        // ChartIQ only checks the 3rd bar after ATH, not all 3 bars
        if (boxState === "none") {
          if (i === allTimeHighIndex + 3) {
            if (
              !results[allTimeHighIndex + 2].darvasBox &&
              !results[allTimeHighIndex + 1].darvasBox &&
              !results[allTimeHighIndex].darvasBox &&
              data[allTimeHighIndex].high > quote.high
            ) {
              boxState = "high";
            }
          }
        }

        // State: "high" -> waiting for low confirmation
        if (boxState === "high") {
          if (quote.high > data[allTimeHighIndex].high) {
            boxState = "none";
          } else if (i >= 3) {
            const low3 = data[i - 3].low;
            const low2 = data[i - 2].low;
            const low1 = data[i - 1].low;
            const low0 = quote.low;

            if (low3 < low2 && low3 < low1 && low3 < low0) {
              // Darvas box established
              boxData = {
                State: 1,
                High: data[allTimeHighIndex].high,
                Low: low3,
                Start: allTimeHighIndex,
              };
              results[allTimeHighIndex].darvasBox = { ...boxData };
              boxState = "darvas";

              // Cancel previous ghost if overlapping
              if (ghost) {
                if (ghost.End > i && data[ghost.Start]) {
                  results[i].ghostBox = { ...results[ghost.Start].ghostBox };
                  if (results[i].ghostBox) {
                    results[i].ghostBox.End = i;
                  }
                  if (data[ghost.End] && results[ghost.End]) {
                    results[ghost.End].ghostBox = null;
                  }
                }
                if (results[ghost.Start] && results[ghost.Start].ghostBox) {
                  results[ghost.Start].ghostBox.State = 0;
                  results[ghost.Start].ghostBox.End = i;
                }
                ghost = null;
              }

              buyLevel = boxData.High + levelOffset;
              if (!sellLevel || sellLevel < boxData.Low - levelOffset) {
                sellLevel = boxData.Low - levelOffset;
              }
            }
          }
        }

        // State: "darvas" -> box is active
        if (boxState === "darvas") {
          // Check upward breakout (creates ghost)
          // ChartIQ uses else-if chain: only check downward if no upward breakout
          if (quote.close > boxData.High) {
            ghost = ghostBoxes ? {} : null;
          } else if (exitField === "high/low" && quote.high > boxData.High) {
            ghost = ghostBoxes ? {} : null;
          } else if (quote.close < boxData.Low) {
            // Downward breakout
            boxState = "none";
          } else if (exitField === "high/low" && quote.low < boxData.Low) {
            // Downward breakout
            boxState = "none";
          }

          // If ghost created, close box
          if (ghost) {
            boxState = "none";
          } else if (boxState === "none") {
            // Box closed without ghost, clear levels
            buyLevel = null;
            sellLevel = null;
          }

          if (!ghostBoxes) {
            ghost = null;
          }

          // Close current Darvas box
          if (boxState === "none") {
            for (let d = boxData.Start + 1; d < i; d++) {
              results[d].darvasBox = { ...boxData };
            }
            boxData.State = 0;
            boxData.End = i;
            results[i].darvasBox = { ...boxData };
            results[i].athIndex = allTimeHighIndex;
            continue;
          }
        }

        // ChartIQ: Check sell level to cancel ghost boxes
        // This runs AFTER darvas block, handles residual levels
        if (sellLevel !== null && boxData.Low != null) {
          if (
            quote.close < boxData.Low ||
            (exitField === "high/low" && quote.low < boxData.Low)
          ) {
            // If still in darvas state, exit (already handled above, shouldn't happen)
            if (boxState === "darvas") {
              boxState = "none";
            }
            // Clear levels if sell level broken
            if (
              quote.close < sellLevel ||
              (exitField === "high/low" && quote.low < sellLevel)
            ) {
              buyLevel = null;
              sellLevel = null;
            }
            // Cancel active ghost box
            if (ghost && ghost.Start != null) {
              if (ghost.End > i && results[ghost.Start]) {
                results[i].ghostBox = { ...results[ghost.Start].ghostBox };
                if (results[i].ghostBox) {
                  results[i].ghostBox.End = i;
                  results[i].ghostBox.State = 0;
                }
                if (ghost.End < data.length && results[ghost.End]) {
                  results[ghost.End].ghostBox = null;
                }
              }
              if (results[ghost.Start] && results[ghost.Start].ghostBox) {
                results[ghost.Start].ghostBox.End = i;
              }
              ghost = null;
            }
          }
        }
      }

      // Update all-time high
      if (data[allTimeHighIndex] && quote.high >= data[allTimeHighIndex].high) {
        allTimeHighIndex = i;
      }

      // Check if need to recalculate ATH within lookback period
      // ChartIQ: if(i<3 || (high >= last 3 highs))
      const isEarlyBar = i < 3;
      const isNewHigh =
        i >= 3 &&
        quote.high >= data[i - 1].high &&
        quote.high >= data[i - 2].high &&
        quote.high >= data[i - 3].high;

      if (isEarlyBar || isNewHigh) {
        if (i - allTimeHighIndex >= athLookbackPeriod) {
          allTimeHighIndex = i;
          for (let j = 0; j < athLookbackPeriod; j++) {
            if (i - j < 0) break;
            if (data[i - j].high > data[allTimeHighIndex].high) {
              allTimeHighIndex = i - j;
            }
          }
        }
      }

      // Detect volume spike at ATH
      if (volumeSpike && i > athLookbackPeriod && i === allTimeHighIndex) {
        if (
          volumeAverage &&
          volumeAverage[i] &&
          volumeAverage[i] * spikePercentage < quote.volume
        ) {
          results[i].volumeSpikeMarker = 1;
        }
      }

      results[i].athIndex = allTimeHighIndex;
    }

    return results;
  }

  calculator.undefinedLength = () => 3; // Minimum 3 bars for confirmation

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
